/**
 * One version of a case, picked in the tree of the Cases page, with the actions on it and
 * on the whole case. A new version starts from this one and is saved next to it.
 */
import { Archive, ArchiveRestore, Bot, CircleCheck, Pencil } from 'lucide-react';
import { useState } from 'react';
import Button from '../../../../components/Button';
import InlineErrorDisplay from '../../../../components/InlineErrorDisplay';
import type { EvalCaseDetail, EvalCaseVersion } from '../../types/evalTypes';
import { formatDateTime, versionAuthor } from '../../utils/evalFormat';
import { CaseExpectView, CaseInputView } from '../run/CaseViews';
import { VersionArchiveControl } from '../VersionArchiveControl';
import { CaseEditor, type CaseDraft } from './CaseEditor';
import { CaseStatusBadge } from './CaseStatusBadge';

interface CasePanelProps {
  evalCase: EvalCaseDetail;
  version: EvalCaseVersion;
  /** The suite's case options, for the form. */
  caseOptions: Record<string, string[]>;
  currentUid: string | undefined;
  /** All throw on failure. The page selects a new version once it is saved. */
  onCreate: (draft: CaseDraft) => Promise<void>;
  onMakeCurrent: (versionId: string) => Promise<void>;
  onSetArchived: (archived: boolean) => Promise<void>;
  /** Archives (with a reason) or restores one version. */
  onSetVersionArchived: (versionId: string, archived: boolean, reason?: string) => Promise<void>;
}

export const CasePanel = ({ evalCase, version, caseOptions, currentUid, onCreate, onMakeCurrent, onSetArchived, onSetVersionArchived }: CasePanelProps) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCurrent = evalCase.current?.versionId === version.versionId;

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : fallback);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-4 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
            <span className="font-mono">{evalCase.caseId}</span>
            <span>v{version.number}</span>
            {isCurrent && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">current</span>}
            {version.archived && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700">archived</span>}
            <CaseStatusBadge evalCase={evalCase} />
          </h2>
          <p className="text-xs text-gray-600">
            {formatDateTime(version.createdAt)}, {versionAuthor(version, currentUid)}
            {version.tokenName !== undefined && <Bot size={12} className="inline ml-1 text-violet-700" aria-label="Proposed by Claude" />} ·{' '}
            <span className="font-mono">{version.hash}</span>
            {!evalCase.current && <span className="text-amber-800"> · No current version: no run uses this case</span>}
          </p>
        </div>
        {evalCase.archived ? (
          <Button variant="outline" size="sm" icon={<ArchiveRestore size={13} />} onClick={() => void run(() => onSetArchived(false), 'Failed to restore the case.')} disabled={busy || editing}>
            Restore case
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            icon={<Archive size={13} />}
            title="New runs skip the case. Old runs keep their copy, and you can restore it."
            onClick={() => void run(() => onSetArchived(true), 'Failed to archive the case.')}
            disabled={busy || editing}
          >
            Archive case
          </Button>
        )}
      </div>

      {editing ? (
        <CaseEditor
          suiteId={evalCase.suiteId}
          caseOptions={caseOptions}
          from={{ caseId: evalCase.caseId, version }}
          onCancel={() => setEditing(false)}
          onSave={async (draft) => {
            await onCreate(draft);
            setEditing(false);
          }}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-2 border-t border-gray-100 pt-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-900">{version.description}</p>
              <p className="text-xs text-gray-600">
                {version.tags.length > 0 && <>{version.tags.join(', ')}</>}
                {version.note && <> · {version.note}</>}
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {!isCurrent && !version.archived && (
                <Button
                  variant="outline"
                  size="sm"
                  icon={<CircleCheck size={13} />}
                  title="Runs queued from now on use this version"
                  onClick={() => void run(() => onMakeCurrent(version.versionId), 'Failed to make it current.')}
                  disabled={busy}
                  loading={busy}
                >
                  Make current
                </Button>
              )}
              {!version.archived && (
                <VersionArchiveControl
                  archived={undefined}
                  isCurrent={isCurrent}
                  currentUid={currentUid}
                  onArchive={(reason) => onSetVersionArchived(version.versionId, true, reason)}
                  onRestore={() => onSetVersionArchived(version.versionId, false)}
                />
              )}
              <Button variant="primary" size="sm" icon={<Pencil size={13} />} onClick={() => setEditing(true)} disabled={busy}>
                New version from this
              </Button>
            </div>
          </div>
          {version.archived && (
            <VersionArchiveControl
              archived={version.archived}
              isCurrent={false}
              currentUid={currentUid}
              onArchive={(reason) => onSetVersionArchived(version.versionId, true, reason)}
              onRestore={() => onSetVersionArchived(version.versionId, false)}
            />
          )}
          {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Input</h3>
              <CaseInputView suiteId={evalCase.suiteId} input={version.input} />
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Expected</h3>
              <CaseExpectView suiteId={evalCase.suiteId} expect={version.expect} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
